import { SelectOption as Option } from 'components/select/types';
import { Size } from 'models/sizes';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSizeDataService } from 'services/size-data.service';
import { BaseAsyncSelect } from '../../BaseAsyncSelect';
import { cn } from 'utils/classNamesTailwind';

const MIN_REQUIRED_CHARACTERS = 1;

const mapSizeToOption = (size: Size): Option<string> => ({
  id: size.sizeCode,
  value: size.sizeCode,
  label: size.sizeCode,
});

export function SizeSelect({
  selectedSizes = undefined,
  setSizes = undefined,
  className = undefined,
  showConfirmButton = false,
}: {
  selectedSizes?: string[];
  setSizes?: (value: string[]) => void;
  className?: string;
  showConfirmButton?: boolean;
}) {
  const intl = useIntl();
  const { fetchSizeDataResponse } = useSizeDataService();

  const label = intl.formatMessage({
    id: 'filter.key.selectedSizes',
    defaultMessage: 'Sizes',
  });

  const placeholder = intl.formatMessage({
    id: 'size.search_placeholder',
    defaultMessage: 'Search sizes...',
  });

  const fetchSizes = useCallback(
    async (searchTerm: string) => {
      const response = await fetchSizeDataResponse(searchTerm);
      return response.data?.sizes ?? [];
    },
    [fetchSizeDataResponse]
  );

  return (
    <BaseAsyncSelect<Size>
      selectedItems={selectedSizes ? selectedSizes.map(code => ({ sizeCode: code })) : null}
      onSelectionChange={sizes => setSizes?.(sizes?.map(s => s.sizeCode) ?? [])}
      fetchItems={fetchSizes}
      getItemId={size => size.sizeCode}
      itemToOption={mapSizeToOption}
      minSearchCharacters={MIN_REQUIRED_CHARACTERS}
      label={label}
      placeholder={placeholder}
      showConfirmButton={showConfirmButton}
      id="size-filter"
      className={cn('w-full', className)}
    />
  );
}
